import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';

interface EligibleInvoice {
  id: string;
  invoice_number: string | null;
  title: string;
  total_amount: number | null;
  paid_amount: number | null;
  credit_applied: number | null;
  status: string;
  estimate_id: string;
  project_id?: string;
}

interface CreditSourceInfo {
  projectId: string | null;
  projectStatus: string | null;
  projectName: string | null;
}

interface UseCreditEligibleInvoicesResult {
  eligibleInvoices: EligibleInvoice[];
  isLoading: boolean;
  creditSourceInfo: CreditSourceInfo | null;
  isCreditLocked: boolean;
  lockedToProjectName: string | null;
}

/**
 * Hook to get eligible invoices for a credit based on project-scoping rules:
 * - Credit is locked to its source project until:
 *   1. Source project status is 'completed', 'cancelled', or 'on_hold', OR
 *   2. All invoices in source project have balance <= 0
 */
export const useCreditEligibleInvoices = (
  creditId: string | null,
  customerId: string | null
): UseCreditEligibleInvoicesResult => {
  // First, get the credit's source project info
  const { data: creditSourceInfo, isLoading: loadingSource } = useQuery({
    queryKey: ['creditSourceInfo', creditId],
    queryFn: async () => {
      if (!creditId) return null;

      // Get credit -> source_invoice -> estimate -> project chain
      const { data: credit, error: creditError } = await supabase
        .from('customer_credits')
        .select('source_invoice_id')
        .eq('id', creditId)
        .single();

      if (creditError || !credit?.source_invoice_id) {
        // Credit has no source invoice - it's unlocked
        return { projectId: null, projectStatus: null, projectName: null };
      }

      // Get invoice -> estimate
      const { data: invoice, error: invoiceError } = await supabase
        .from('invoices')
        .select('estimate_id')
        .eq('id', credit.source_invoice_id)
        .single();

      if (invoiceError || !invoice?.estimate_id) {
        return { projectId: null, projectStatus: null, projectName: null };
      }

      // Get estimate -> project
      const { data: project, error: projectError } = await supabase
        .from('projects')
        .select('id, status, name')
        .eq('estimate_id', invoice.estimate_id)
        .maybeSingle();

      if (projectError || !project) {
        return { projectId: null, projectStatus: null, projectName: null };
      }

      return {
        projectId: project.id,
        projectStatus: project.status,
        projectName: project.name,
      };
    },
    enabled: !!creditId,
  });

  // Check if all invoices in the source project are fully paid
  const { data: projectInvoicesPaid, isLoading: loadingProjectInvoices } = useQuery({
    queryKey: ['projectInvoicesPaid', creditSourceInfo?.projectId],
    queryFn: async () => {
      if (!creditSourceInfo?.projectId) return true; // No project = unlocked

      // Get all invoices for this project via estimate
      const { data: invoices, error } = await supabase
        .from('invoices')
        .select('id, total_amount, paid_amount, status, estimates!inner(project_id)')
        .neq('status', 'cancelled');

      if (error) {
        console.error('Error fetching project invoices:', error);
        return false;
      }

      // Filter to this project's invoices
      const projectInvoices = invoices?.filter(
        (inv: any) => inv.estimates?.project_id === creditSourceInfo.projectId
      );

      if (!projectInvoices || projectInvoices.length === 0) return true;

      // Check if all have balance <= 0 (accounting for credits)
      return projectInvoices.every(
        (inv: any) => {
          const netAmount = (inv.total_amount || 0) - (inv.credit_applied || 0);
          return netAmount - (inv.paid_amount || 0) <= 0;
        }
      );
    },
    enabled: !!creditSourceInfo?.projectId,
  });

  // Determine if credit is locked to its source project
  const isCreditLocked = (() => {
    if (!creditSourceInfo?.projectId) return false;
    
    const unlockStatuses = ['completed', 'cancelled', 'on_hold'];
    const statusUnlocked = unlockStatuses.includes(creditSourceInfo.projectStatus || '');
    const allPaid = projectInvoicesPaid === true;
    
    return !statusUnlocked && !allPaid;
  })();

  // Fetch eligible invoices based on credit lock status
  const { data: eligibleInvoices = [], isLoading: loadingInvoices } = useQuery({
    queryKey: ['eligibleInvoices', customerId, creditSourceInfo?.projectId, isCreditLocked],
    queryFn: async () => {
      if (!customerId) return [];

      // Base query for unpaid invoices for this customer (include drafts)
      let query = supabase
        .from('invoices')
        .select(`
          id, 
          invoice_number, 
          title, 
          total_amount, 
          paid_amount,
          credit_applied,
          status, 
          estimate_id,
          estimates!inner(project_id)
        `)
        .eq('customer_id', customerId)
        .in('status', ['draft', 'sent', 'partially_paid', 'overdue'])
        .order('created_at', { ascending: false });

      const { data, error } = await query;

      if (error) {
        console.error('Error fetching eligible invoices:', error);
        return [];
      }

      // Filter to only include invoices with a positive net balance
      let invoices = (data || [])
        .map((inv: any) => ({
          id: inv.id,
          invoice_number: inv.invoice_number,
          title: inv.title,
          total_amount: inv.total_amount,
          paid_amount: inv.paid_amount,
          credit_applied: inv.credit_applied,
          status: inv.status,
          estimate_id: inv.estimate_id,
          project_id: inv.estimates?.project_id,
        }))
        .filter((inv: EligibleInvoice) => {
          // Only include invoices with positive net balance after credits
          const netAmount = (inv.total_amount || 0) - (inv.credit_applied || 0);
          const balance = netAmount - (inv.paid_amount || 0);
          return balance > 0;
        });

      // If credit is locked, filter to only source project's invoices
      if (isCreditLocked && creditSourceInfo?.projectId) {
        invoices = invoices.filter(
          (inv) => inv.project_id === creditSourceInfo.projectId
        );
      }

      return invoices;
    },
    enabled: !!customerId && creditSourceInfo !== undefined,
  });

  return {
    eligibleInvoices,
    isLoading: loadingSource || loadingProjectInvoices || loadingInvoices,
    creditSourceInfo: creditSourceInfo || null,
    isCreditLocked,
    lockedToProjectName: isCreditLocked ? creditSourceInfo?.projectName || null : null,
  };
};

export default useCreditEligibleInvoices;
